import { useEffect, useState, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const TIMEOUT_MS = 30000;
const MAX_RETRIES = 2;

/**
 * SubcategoryRoute - Validates subcategory and parent category are active
 * Returns 404 if either is inactive. Retries on 429/timeout to reduce "failed to load".
 */
function SubcategoryRoute({ children }) {
  const { categorySlug, subcategorySlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [subcategory, setSubcategory] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!subcategorySlug) {
      setLoading(false);
      setIsValid(false);
      return;
    }

    let cancelled = false;

    const validateSubcategory = async (attempt = 0) => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      try {
        const response = await fetch(`${API_URL}/subcategories/slug/${subcategorySlug}`, {
          cache: 'no-store',
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;

        const data = await response.json();

        if (data.success && data.subcategory) {
          if (data.subcategory.categoryId?.slug === categorySlug) {
            setIsValid(true);
            setSubcategory(data.subcategory);
          } else {
            setIsValid(false);
          }
        } else {
          setIsValid(false);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (attempt < MAX_RETRIES && error.name !== 'AbortError') {
          const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
          await new Promise((r) => setTimeout(r, delay));
          return validateSubcategory(attempt + 1);
        }
        if (process.env.NODE_ENV === 'development') {
          console.error('Subcategory validation error:', error);
        }
        setIsValid(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    validateSubcategory();

    return () => {
      cancelled = true;
      if (abortRef.current) abortRef.current.abort();
    };
  }, [categorySlug, subcategorySlug]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '50vh' 
      }}>
        <div className="auth-spinner"></div>
      </div>
    );
  }

  if (!isValid) {
    return <Navigate to="/404" replace />;
  }

  return (
    <>
      {subcategory && (
        <Helmet>
          <title>{subcategory.name} — Luvia</title>
        </Helmet>
      )}
      {children}
    </>
  );
}

export default SubcategoryRoute;
